// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { generateSearchTextFieldStylesWithLeftIcon } from "@bg/App/SharedStyles/GeneralStyles";
import BrandAlphabetNavigation, {
    BrandAlphabetNavigationStyles,
} from "@bg/Overrides/Widgets/Brand/BrandAlphabetNavigation";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { xScreen } from "@bg/XDL/Utilities";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandAlphabetLetterModel, BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import VerticalColumnCell, { VerticalColumnCellProps } from "@insite/content-library/Components/VerticalColumnCell";
import VerticalColumnContainer, {
    VerticalColumnContainerProps,
} from "@insite/content-library/Components/VerticalColumnContainer";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    allBrandsDataView: getAllBrandsDataView(state),
    brandAlphabetState: state.pages.brands.brandAlphabetState,
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

type BrandLetterMap = {
    [letter: string]: BrandModel[];
};

export interface BrandListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: InjectableCss;
    brandAlphabetNavigation?: BrandAlphabetNavigationStyles;
    brandAccordion?: AccordionProps;
    brandAccordionSection?: AccordionSectionPresentationProps;
    verticalColumnContainer?: VerticalColumnContainerProps;
    verticalColumnCell?: VerticalColumnCellProps;
    brandLink?: LinkPresentationProps;
    actions?: InjectableCss;
    actionLinks?: LinkPresentationProps;
    actionLinksDisabled?: TypographyPresentationProps;
    buttons?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    wrapperCss?: InjectableCss;
    titleCss?: InjectableCss;
    searchWrapper?: InjectableCss;
    filterBrands?: TextFieldPresentationProps;
}

export const listStyles: BrandListStyles = {
    container: {
        css: css`
            .brand-acc {
                padding-bottom: 90px;
                button {
                    border-width: 2px !important;
                    margin-bottom: 8px;
                    &:focus {
                        border: 0 !important;
                        border-top: 2px solid ${getColor("common.ui3")} !important;
                    }
                }
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    brandAccordionSection: {
        // TODO: XNG, we need to fix overrides here
        titleTypographyProps: {
            transform: "uppercase",
            forwardAs: "h5",
            css: css`
                font-weight: ${fontConfig.weight.bold};
                color: ${getColor("text.main")};
                line-height: 25px;
                letter-spacing: 0.23px;
                font-size: 23px;
            `,
        },
    },
    backToTopButton: {
        variant: "secondary",
        sizeVariant: "large",
        css: css`
            position: absolute;
            box-shadow: 0 0 25px 0 rgba(79, 79, 119, 0.18);
            height: 40px;
            width: 190px;
            padding-right: 8px !important;
            padding-left: 8px !important;
            top: 0;
            left: 30px;
            z-index: 100;
            svg {
                fill: ${getColor("primary")};
            }
            span {
                color: ${getColor("secondary")};
                text-transform: capitalize;
                line-height: 16px;
            }
            ${xScreen("lte", "sm")} {
                position: static;
            }
        `,
    },
    brandLink: {
        css: css`
            width: 100%;
        `,
    },
    verticalColumnContainer: {
        columnCounts: [1, 2, 2, 4, 4],
    },
    actions: {
        css: css`
            display: flex;
            justify-content: flex-end;
            padding: 5px;
        `,
    },
    actionLinks: {
        css: css`
            margin: 5px;
        `,
    },
    actionLinksDisabled: {
        css: css`
            margin: 5px;
        `,
        color: "text.disabled",
    },
    buttons: {
        css: css`
            display: flex;
            justify-content: flex-end;
            position: absolute;
            height: 42px;
            left: 0;
            ${xScreen("lte", "sm")} {
                position: static;
                justify-content: flex-start;
                height: 55px;
            }
        `,
    },
    wrapperCss: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            ${xScreen("lte", "xs")} {
                margin-left: 20px;
            }
        `,
    },
    searchWrapper: {
        css: css`
            min-width: 317px;
            input {
                padding-left: 32px !important;
            }
            ${xScreen("lte", "xs")} {
                margin-top: 15px;
            }
        `,
    },

    filterBrands: generateSearchTextFieldStylesWithLeftIcon(),
};

const styles = listStyles;

interface BrandListState {
    expanded: boolean | "mixed";
    brandLetterDetails: BrandAlphabetLetterModel[];
    brandSections: BrandSection[];
}

/**
 * This will display all Brands grouped by the first character.
 * Starting with numbers grouped into '#'.
 * @param props
 */
const BrandList: FC<Props> = (props: Props) => {
    const { allBrandsDataView, brandAlphabetState } = props;
    const [filterBrands, setFilterBrands] = React.useState("");
    const [hashLink, setHashLink] = React.useState({
        linkable: false,
        letter: "#",
    });
    const brandList = allBrandsDataView.value || [];
    const brandAlphabet = brandAlphabetState.value || [];
    const { letters, brandLettersMap } = buildLettersAndMap(brandList);
    const { brandLetterDetails, brandSections } = buildBrandSections(
        letters,
        brandLettersMap,
        brandAlphabet,
        filterBrands,
        false,
    );

    const [state, setState] = React.useState<BrandListState>({ expanded: false, brandLetterDetails, brandSections });
    const [lastLetterClicked, setlastLetterClicked] = React.useState("");

    React.useEffect(() => {
        setState({ ...state, brandLetterDetails, brandSections });
        if (state.brandSections && state.brandSections.length > 0) {
            const hashItem = state.brandSections.find((item, index) => item.letter === "#");

            if (hashItem) {
                setHashLink({
                    linkable: true,
                    letter: "#",
                });
            }
        }
    }, [brandLetterDetails?.length, brandSections?.length]);

    React.useEffect(() => {
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // React.useLayoutEffect(() => {
    //     if (lastLetterClicked) {
    //         const letterElement = document.getElementById(`letter-${lastLetterClicked}`);
    //         if (letterElement) {
    //             const letterOffset = letterElement.offsetTop;
    //             if (letterOffset > 0) {
    //                 window.scrollTo(0, letterOffset);
    //             }
    //         }
    //         setlastLetterClicked("");
    //     }
    // }, [lastLetterClicked]);

    const handleResize = () => {
        const footer = document.getElementsByTagName("footer")[0];
        if (footer) {
            if (window?.innerWidth < 580) {
                footer.style.marginTop = "-6px";
            } else {
                footer.style.marginTop = "47px";
            }
        }
    };

    if (allBrandsDataView.isLoading || brandAlphabetState.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }
    if (brandList.length === 0) {
        return null;
    }

    const onClickBrandSection =
        (section: BrandSection, letterClicked = false) =>
        () => {
            setState(({ brandSections, expanded, brandLetterDetails }: BrandListState) => {
                const itemIndex = brandSections.findIndex(item => item.letter === section.letter);
                const nextSectionExpanded = letterClicked ? true : !section.expanded;
                let nextExpanded = expanded;
                if (expanded !== "mixed" && expanded !== nextSectionExpanded) {
                    nextExpanded = "mixed";
                } else if (expanded === "mixed") {
                    const hasNonMatchingExpanded = brandSections.find(item => {
                        return item.letter !== section.letter && item.expanded !== nextSectionExpanded;
                    });
                    if (!hasNonMatchingExpanded) {
                        nextExpanded = nextSectionExpanded;
                    }
                }
                const newSectionState = { ...section, expanded: nextSectionExpanded };
                const newBrandSections = [...brandSections];
                newBrandSections.splice(itemIndex, 1, newSectionState);
                return { brandSections: newBrandSections, expanded: nextExpanded, brandLetterDetails };
            });
        };

    const onCollapse = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (state.expanded === "mixed" || state.expanded) {
            const { letters, brandLettersMap } = buildLettersAndMap(brandList);
            const { brandLetterDetails, brandSections } = buildBrandSections(
                letters,
                brandLettersMap,
                brandAlphabet,
                "",
                false,
            );
            setState({ ...state, brandLetterDetails, brandSections, expanded: false });
        }
    };

    const onExpand = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (state.expanded === "mixed" || !state.expanded) {
            const { letters, brandLettersMap } = buildLettersAndMap(brandList);
            const { brandLetterDetails, brandSections } = buildBrandSections(
                letters,
                brandLettersMap,
                brandAlphabet,
                "",
                true,
            );
            setState({ ...state, brandLetterDetails, brandSections, expanded: true });
        }
    };

    const handleBackToTop = () => {
        window.scrollTo(0, 0);
    };

    const handleBrandLetterClick = (letter: string) => {
        const section = state.brandSections.find(item => item.letter === letter);
        section && onClickBrandSection(section, true)();
        setlastLetterClicked(letter || "");
        const letterElement = document.getElementById(`letter-${letter}`);
        const headerElement = document.querySelector("#header");
        if (letterElement) {
            const { top } = letterElement.getBoundingClientRect();
            const headerHeight = headerElement ? headerElement.getBoundingClientRect().height : 0;
            setTimeout(() => {
                window.scrollTo(0, top + window.pageYOffset - headerHeight);
            }, 200);
        }
    };

    const handleFilterBrands = (e: any) => {
        const target = e.target;
        const value = target.value;

        setFilterBrands(value);

        const brandSections = buildBrandSections(letters, brandLettersMap, brandAlphabet, value);

        const optimizedBrandSections = brandSections.brandSections.map(item => {
            if (item.brandMap.length > 0 && value !== "") {
                item.expanded = true;
            }
            return item;
        });

        setState({
            ...state,
            brandLetterDetails: brandSections.brandLetterDetails,
            brandSections: value !== "" ? optimizedBrandSections : brandSections.brandSections,
        });
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="brandList">
            <StyledWrapper {...styles.wrapperCss}>
                <Typography variant="h1" {...styles.titleCss}>
                    {translate("Brands")}
                </Typography>
                <StyledWrapper {...styles.searchWrapper}>
                    <TextField
                        name="filterBrands"
                        value={filterBrands}
                        placeholder={translate("Search Brands")}
                        onChange={handleFilterBrands}
                        {...styles.filterBrands}
                    />
                </StyledWrapper>
            </StyledWrapper>

            <BrandAlphabetNavigation
                expanded={state.expanded === true}
                onExpand={onExpand}
                letterDetails={brandLetterDetails}
                extendedStyles={styles.brandAlphabetNavigation}
                onBrandLetterClick={handleBrandLetterClick}
                hashLink={hashLink}
            />
            <StyledWrapper {...styles.actions}>
                {state.expanded === false ? (
                    <Link
                        onClick={onExpand}
                        {...styles.actionLinks}
                        id="expandButton"
                        data-test-selector="brandListExpandAllLink"
                    >
                        {translate("Expand All")}
                    </Link>
                ) : (
                    <Link onClick={onCollapse} {...styles.actionLinks} data-test-selector="brandListCollapseAllLink">
                        {translate("Collapse All")}
                    </Link>
                )}
            </StyledWrapper>

            <Accordion className="brand-acc" headingLevel={2}>
                {state.brandSections.map((brandDetails, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper key={brandDetails.letter.toString() + index}>
                        <span id={`letter-${brandDetails.letter}`}></span>
                        <AccordionSection
                            key={brandDetails.letter}
                            {...styles.brandAccordionSection}
                            title={brandDetails.letter}
                            expanded={brandDetails.expanded}
                            onTogglePanel={onClickBrandSection(brandDetails)}
                        >
                            <VerticalColumnContainer
                                {...styles.verticalColumnContainer}
                                data-test-selector="brandSection"
                            >
                                {brandDetails.brandMap.map(brand => (
                                    <VerticalColumnCell key={brand.detailPagePath} {...styles.verticalColumnCell}>
                                        <Link
                                            {...styles.brandLink}
                                            href={brand.detailPagePath}
                                            typographyProps={{ ellipsis: true, as: "h3" } as any}
                                            data-test-selector="brandLink"
                                        >
                                            {brand.name}
                                        </Link>
                                    </VerticalColumnCell>
                                ))}
                            </VerticalColumnContainer>
                        </AccordionSection>
                    </StyledWrapper>
                ))}
            </Accordion>
            <StyledWrapper {...styles.buttons}>
                <Button
                    aria-label="tertiary"
                    {...styles.backToTopButton}
                    icon={{
                        position: "left",
                        src: "ArrowUp",
                    }}
                    variant="tertiary"
                    onClick={handleBackToTop}
                >
                    {translate("Return to Top")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BrandList),
    definition: {
        group: "Brands",
        icon: "List",
        allowedContexts: ["BrandsPage"],
    },
};

export default widgetModule;

interface BrandSection {
    letter: string;
    brandMap: BrandModel[];
    expanded: boolean;
}

/**
 * Takes in a brand list and sorts it into #, A-Z, Other characters.
 *
 * @param brandList The list of brands that should be sorted.
 */
const buildLettersAndMap = (brandList: BrandModel[]): { letters: string[]; brandLettersMap: BrandLetterMap } => {
    let letters: string[] = [];
    let letter: string;
    let newLetters: string[] = letters;
    const brandLettersMap: BrandLetterMap = {};
    for (let i = 0; i < brandList.length; i = i + 1) {
        const brand = brandList[i];
        letter = brand.name[0] ? brand.name[0].toLowerCase() : "";
        if (isNumeric(letter)) {
            letter = "#";
        }
        if (!brandLettersMap[letter]) {
            brandLettersMap[letter] = [];
        }
        brandLettersMap[letter].push(brand);
        newLetters = [...newLetters, letter];
    }
    letters = newLetters.filter((letter, index, array) => array.indexOf(letter) === index);
    if (brandLettersMap["#"]) {
        brandLettersMap["#"].sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));
    }

    return { letters, brandLettersMap };
};

/**
 * Take in the props and create easier to work with state objects.
 *
 * @param letters List of letters and special characters.
 * @param brandLettersMap Brand list sorted into letter lists.
 * @param brandAlphabet Letter count list.
 * @param expanded The expanded value to use if not undefined.
 */
const buildBrandSections = (
    letters: string[],
    brandLettersMap: BrandLetterMap,
    brandAlphabet: BrandAlphabetLetterModel[],
    search?: string | undefined,
    expanded?: boolean,
): {
    brandLetterDetails: {
        count: number;
        letter: string;
        linkable: boolean;
    }[];
    brandSections: {
        letter: string;
        brandMap: BrandModel[];
        expanded: boolean;
    }[];
} => {
    const brandLetterDetails = brandAlphabet.map(alphabetItem => ({
        ...alphabetItem,
        linkable: alphabetItem.count > 0,
    }));

    const brandSections = letters.map(letter => ({
        letter,
        brandMap: getBrandLettersMap(brandLettersMap, letter, search),
        expanded: expanded || search?.includes(letter) || false,
    }));

    return { brandLetterDetails, brandSections };
};

const getBrandLettersMap = (brandLettersMap: BrandLetterMap, letter: string, search?: string | undefined) => {
    if (!search) {
        return brandLettersMap[letter];
    }

    return brandLettersMap[letter].filter(brand => (brand.name || "").toLowerCase().includes(search.toLowerCase()));
};
